import { useState } from "react";
import { Key } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-black/40 border border-white/10 focus:border-white/30 text-white rounded-none px-3 py-2.5 text-sm focus:outline-none transition-colors";

export const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setError("");
    setSuccess(false);
  };

  const handleOpenChange = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) {
      setError("New passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      setSuccess(true);
      setTimeout(() => handleOpenChange(false), 1800);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Failed to update password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="change-password-dialog"
        className="bg-[#0B0B0F] border border-white/10 text-white max-w-sm"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <Key size={20} weight="light" className="text-[#F59E0B]" />
            Change Password
          </DialogTitle>
          <DialogDescription className="text-white/50 text-sm">
            Update your admin password. You'll stay signed in.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div
            data-testid="change-password-success"
            className="text-sm text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded px-3 py-2"
          >
            Password updated successfully.
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            {error && (
              <div
                data-testid="change-password-error"
                className="text-sm text-[#FF6B6B] bg-[#8A1538]/10 border border-[#8A1538]/30 rounded px-3 py-2"
              >
                {error}
              </div>
            )}
            <div>
              <label className="block text-xs uppercase tracking-wider text-white/40 mb-1">
                Current password
              </label>
              <input
                data-testid="current-password-input"
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                autoComplete="current-password"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-white/40 mb-1">
                New password
              </label>
              <input
                data-testid="new-password-input"
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                className={inputCls}
              />
              <p className="text-[11px] text-white/30 mt-1">At least 8 characters.</p>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-white/40 mb-1">
                Confirm new password
              </label>
              <input
                data-testid="confirm-password-input"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                className={inputCls}
              />
            </div>
            <button
              data-testid="change-password-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-[#F59E0B] hover:bg-[#d97706] disabled:opacity-50 text-black font-bold text-xs uppercase tracking-[0.18em] py-3 transition-colors"
            >
              {loading ? "Updating..." : "Update Password"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
